import type { Language } from "./languages"

/**
 * Minimal RFC 4647-ish Accept-Language negotiation: returns the available
 * language with the highest q-value, matching on the primary subtag,
 * or undefined when no available language appears with q > 0.
 * Total: never throws on malformed input (malformed q defaults to 1).
 */
export const preferredLanguage = (
  header: string | null | undefined,
  available: readonly Language[]
): Language | undefined => {
  if (!header) return undefined

  const scores = new Map<Language, number>()

  for (const part of header.split(",")) {
    const [rawTag, ...params] = part.trim().split(";")
    const primary = rawTag?.trim().toLowerCase().split("-")[0]
    if (!primary) continue

    const match = available.find((lang) => lang === primary)
    if (!match) continue

    let q = 1
    for (const param of params) {
      const [key, value] = param.split("=").map((s) => s.trim())
      if (key === "q") {
        const parsed = Number(value)
        // RFC 4647/7231: q is a fixed-point number in [0, 1]. Reject
        // out-of-range or non-finite values as malformed and keep the
        // documented default of 1 (e.g. a bogus q=1.5 must not outrank q=1).
        if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 1) q = parsed
      }
    }

    if (q > (scores.get(match) ?? -1)) scores.set(match, q)
  }

  let best: Language | undefined
  let bestQ = 0
  for (const [lang, q] of scores) {
    if (q > bestQ) {
      best = lang
      bestQ = q
    }
  }
  return best
}
